import React, { useMemo, useState } from "react";
import {
  createLRoomPreset,
  createThreeWallRoomPreset,
  createOpening,
  createRoomDefaults,
  createServicePoint,
  OPENING_TYPES,
  SERVICE_TYPES,
} from "../../room/roomSchema";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function Section({ title, children }) {
  return (
    <div className="border rounded bg-white overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b font-semibold text-sm">
        {title}
      </div>
      <div className="p-3 space-y-3">{children}</div>
    </div>
  );
}

function NumberInput({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(toNumber(e.target.value, 0))}
        className="w-full px-2 py-1.5 border rounded text-sm"
      />
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border rounded text-sm"
      />
    </div>
  );
}

function findRoomObject(room, id) {
  if (!id) return null;

  const walls = room?.walls || [];
  const openings = room?.openings || [];
  const services = room?.services || [];

  const wall = walls.find((x) => String(x.id) === String(id));
  if (wall) return { kind: "wall", item: wall };

  const opening = openings.find((x) => String(x.id) === String(id));
  if (opening) return { kind: "opening", item: opening };

  const service = services.find((x) => String(x.id) === String(id));
  if (service) return { kind: "service", item: service };

  return null;
}

function getWallName(room, wallId) {
  return (
    (room?.walls || []).find((w) => String(w.id) === String(wallId))?.name ||
    wallId ||
    "Wall"
  );
}

export default function RoomEditorPanel({
  room,
  setRoom,
  selectedRoomObjectId,
  setSelectedRoomObjectId,
}) {
  const [presetWidth, setPresetWidth] = useState(room?.width || 3000);
  const [presetDepth, setPresetDepth] = useState(room?.depth || 2200);
  const [presetHeight, setPresetHeight] = useState(room?.height || 2800);

  const safeRoom = room || createRoomDefaults();

  const walls = safeRoom.walls || [];
  const openings = safeRoom.openings || [];
  const services = safeRoom.services || [];

  const selected = useMemo(
    () => findRoomObject(safeRoom, selectedRoomObjectId),
    [safeRoom, selectedRoomObjectId]
  );

  function patchRoom(patch) {
    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      ...patch,
    }));
  }

  function updateWall(id, patch) {
    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      walls: (prev?.walls || []).map((wall) =>
        String(wall.id) === String(id) ? { ...wall, ...patch } : wall
      ),
    }));
  }

  function updateOpening(id, patch) {
    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      openings: (prev?.openings || []).map((opening) =>
        String(opening.id) === String(id) ? { ...opening, ...patch } : opening
      ),
    }));
  }

  function updateService(id, patch) {
    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      services: (prev?.services || []).map((service) =>
        String(service.id) === String(id) ? { ...service, ...patch } : service
      ),
    }));
  }

  function addOpeningToSelectedWall(type) {
    const wallId =
      selected?.kind === "wall"
        ? selected.item.id
        : walls[0]?.id;

    if (!wallId) {
      alert("Create/select wall first.");
      return;
    }

    const next = createOpening({
      wallId,
      type,
      offset: 1000,
      width: type === OPENING_TYPES.DOOR ? 800 : 900,
      height: type === OPENING_TYPES.DOOR ? 2100 : 1100,
      sillHeight: type === OPENING_TYPES.DOOR ? 0 : 900,
    });

    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      openings: [...(prev?.openings || []), next],
    }));

    setSelectedRoomObjectId?.(next.id);
  }

  function addServiceToSelectedWall(type) {
    const wallId =
      selected?.kind === "wall"
        ? selected.item.id
        : walls[0]?.id;

    if (!wallId) {
      alert("Create/select wall first.");
      return;
    }

    const next = createServicePoint({
      wallId,
      type,
      offset: 1000,
      height:
        type === SERVICE_TYPES.WATER || type === SERVICE_TYPES.DRAIN
          ? 500
          : 1100,
    });

    setRoom((prev) => ({
      ...(prev || createRoomDefaults()),
      services: [...(prev?.services || []), next],
    }));

    setSelectedRoomObjectId?.(next.id);
  }

  function removeSelectedObject() {
    if (!selected) return;

    if (selected.kind === "wall") {
      const id = selected.item.id;

      setRoom((prev) => ({
        ...(prev || createRoomDefaults()),
        walls: (prev?.walls || []).filter((x) => String(x.id) !== String(id)),
        openings: (prev?.openings || []).filter(
          (x) => String(x.wallId) !== String(id)
        ),
        services: (prev?.services || []).filter(
          (x) => String(x.wallId) !== String(id)
        ),
      }));
    }

    if (selected.kind === "opening") {
      setRoom((prev) => ({
        ...(prev || createRoomDefaults()),
        openings: (prev?.openings || []).filter(
          (x) => String(x.id) !== String(selected.item.id)
        ),
      }));
    }

    if (selected.kind === "service") {
      setRoom((prev) => ({
        ...(prev || createRoomDefaults()),
        services: (prev?.services || []).filter(
          (x) => String(x.id) !== String(selected.item.id)
        ),
      }));
    }

    setSelectedRoomObjectId?.(null);
  }

  function renderInspector() {
    if (!selected) {
      return (
        <div className="text-sm text-slate-500">
          Select a wall, opening or service from hierarchy or scene.
        </div>
      );
    }

    const item = selected.item;

    if (selected.kind === "wall") {
      return (
        <div className="space-y-3">
          <TextInput
            label="Wall name"
            value={item.name}
            onChange={(value) => updateWall(item.id, { name: value })}
          />

          <div className="grid grid-cols-2 gap-2">
            <NumberInput
              label="Start X"
              value={item.start?.x}
              onChange={(value) =>
                updateWall(item.id, {
                  start: { ...(item.start || {}), x: value },
                })
              }
            />
            <NumberInput
              label="Start Z"
              value={item.start?.z}
              onChange={(value) =>
                updateWall(item.id, {
                  start: { ...(item.start || {}), z: value },
                })
              }
            />
            <NumberInput
              label="End X"
              value={item.end?.x}
              onChange={(value) =>
                updateWall(item.id, {
                  end: { ...(item.end || {}), x: value },
                })
              }
            />
            <NumberInput
              label="End Z"
              value={item.end?.z}
              onChange={(value) =>
                updateWall(item.id, {
                  end: { ...(item.end || {}), z: value },
                })
              }
            />
            <NumberInput
              label="Height"
              value={item.height}
              onChange={(value) => updateWall(item.id, { height: value })}
            />
            <NumberInput
              label="Thickness"
              value={item.thickness}
              onChange={(value) => updateWall(item.id, { thickness: value })}
            />
          </div>

          <div>
            <label className="text-xs text-slate-500">Inward direction</label>
            <select
              value={item.inward || "positive_z"}
              onChange={(e) => updateWall(item.id, { inward: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            >
              <option value="positive_z">positive_z</option>
              <option value="negative_z">negative_z</option>
              <option value="positive_x">positive_x</option>
              <option value="negative_x">negative_x</option>
            </select>
          </div>
        </div>
      );
    }

    if (selected.kind === "opening") {
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Opening type</label>
            <select
              value={item.type}
              onChange={(e) => updateOpening(item.id, { type: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            >
              <option value="window">window</option>
              <option value="door">door</option>
              <option value="passage">passage</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-500">Attached wall</label>
            <select
              value={item.wallId || ""}
              onChange={(e) =>
                updateOpening(item.id, { wallId: e.target.value })
              }
              className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            >
              {walls.map((wall) => (
                <option key={wall.id} value={wall.id}>
                  {wall.name || wall.id}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <NumberInput
              label="Offset"
              value={item.offset}
              onChange={(value) => updateOpening(item.id, { offset: value })}
            />
            <NumberInput
              label="Width"
              value={item.width}
              onChange={(value) => updateOpening(item.id, { width: value })}
            />
            <NumberInput
              label="Height"
              value={item.height}
              onChange={(value) => updateOpening(item.id, { height: value })}
            />
            <NumberInput
              label="Sill height"
              value={item.sillHeight}
              onChange={(value) =>
                updateOpening(item.id, { sillHeight: value })
              }
            />
          </div>
        </div>
      );
    }

    if (selected.kind === "service") {
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Service type</label>
            <select
              value={item.type}
              onChange={(e) => updateService(item.id, { type: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            >
              {Object.values(SERVICE_TYPES).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-500">Attached wall</label>
            <select
              value={item.wallId || ""}
              onChange={(e) => updateService(item.id, { wallId: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            >
              {walls.map((wall) => (
                <option key={wall.id} value={wall.id}>
                  {wall.name || wall.id}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <NumberInput
              label="Offset"
              value={item.offset}
              onChange={(value) => updateService(item.id, { offset: value })}
            />
            <NumberInput
              label="Height"
              value={item.height}
              onChange={(value) => updateService(item.id, { height: value })}
            />
          </div>

          <TextInput
            label="Label"
            value={item.label}
            onChange={(value) => updateService(item.id, { label: value })}
            placeholder="Example: Water supply"
          />
        </div>
      );
    }

    return null;
  }

  return (
    <div className="space-y-4">
      <Section title="Room dimensions">
        <div className="grid grid-cols-3 gap-2">
          <NumberInput label="Width" value={presetWidth} onChange={setPresetWidth} />
          <NumberInput label="Depth" value={presetDepth} onChange={setPresetDepth} />
          <NumberInput label="Height" value={presetHeight} onChange={setPresetHeight} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          
        </div>
        <div className="grid grid-cols-3 gap-2">
  <NumberInput
    label="Base offset"
    value={safeRoom.baseFloorOffset ?? 100}
    onChange={(value) => patchRoom({ baseFloorOffset: value })}
  />

  <NumberInput
    label="Wall offset"
    value={safeRoom.wallCabinetOffset ?? 1500}
    onChange={(value) => patchRoom({ wallCabinetOffset: value })}
  />

  <NumberInput
    label="Ceiling gap"
    value={safeRoom.ceilingClearance ?? 50}
    onChange={(value) => patchRoom({ ceilingClearance: value })}
  />
</div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() =>
              setRoom(
                createThreeWallRoomPreset({
                  width: presetWidth,
                  depth: presetDepth,
                  height: presetHeight,
                })
              )
            }
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
          >
            Create 3-wall room
          </button>

          <button
            type="button"
            onClick={() =>
              setRoom(
                createLRoomPreset({
                  mainWallLength: presetWidth,
                  sideWallLength: presetDepth,
                  height: presetHeight,
                })
              )
            }
            className="px-3 py-2 rounded border bg-white text-sm"
          >
            Create L room
          </button>
        </div>
      </Section>

      <Section title="Add to selected wall">
        <div className="text-xs text-slate-500">
          Select a wall first, then add openings or services.
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => addOpeningToSelectedWall(OPENING_TYPES.WINDOW)}
            className="px-3 py-2 rounded border bg-white text-sm"
          >
            + Window
          </button>

          <button
            type="button"
            onClick={() => addOpeningToSelectedWall(OPENING_TYPES.DOOR)}
            className="px-3 py-2 rounded border bg-white text-sm"
          >
            + Door
          </button>

          {Object.values(SERVICE_TYPES).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => addServiceToSelectedWall(type)}
              className="px-3 py-2 rounded border bg-white text-sm"
            >
              + {type}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Room hierarchy">
        <div className="space-y-2">
          {walls.map((wall) => {
            const wallOpenings = openings.filter(
              (opening) => String(opening.wallId) === String(wall.id)
            );

            const wallServices = services.filter(
              (service) => String(service.wallId) === String(wall.id)
            );

            const isSelected = String(selectedRoomObjectId) === String(wall.id);

            return (
              <div key={wall.id} className="border rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSelectedRoomObjectId?.(wall.id)}
                  className={[
                    "w-full text-left px-3 py-2 text-sm",
                    isSelected ? "bg-blue-50 text-blue-700" : "bg-white",
                  ].join(" ")}
                >
                  <div className="font-medium">{wall.name || wall.id}</div>
                  <div className="text-[11px] text-slate-400">
                    {wall.start?.x},{wall.start?.z} → {wall.end?.x},{wall.end?.z}
                  </div>
                </button>

                <div className="px-3 pb-2 space-y-1">
                  {wallOpenings.map((opening) => (
                    <button
                      key={opening.id}
                      type="button"
                      onClick={() => setSelectedRoomObjectId?.(opening.id)}
                      className={[
                        "w-full text-left px-2 py-1 rounded text-xs",
                        String(selectedRoomObjectId) === String(opening.id)
                          ? "bg-orange-50 text-orange-700"
                          : "hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {opening.type} {opening.width} @ {opening.offset}
                    </button>
                  ))}

                  {wallServices.map((service) => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => setSelectedRoomObjectId?.(service.id)}
                      className={[
                        "w-full text-left px-2 py-1 rounded text-xs",
                        String(selectedRoomObjectId) === String(service.id)
                          ? "bg-yellow-50 text-yellow-700"
                          : "hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {service.type} @ {service.offset} / H {service.height}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Selected room object">
        {renderInspector()}

        {selected && (
          <button
            type="button"
            onClick={removeSelectedObject}
            className="w-full px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm"
          >
            Remove selected object
          </button>
        )}
      </Section>
    </div>
  );
}